import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { AccordionBase, AccordionContent, AccordionItem, AccordionTrigger } from '@/app_components/ui/BasicAccordion';
import { BasicModal } from '@/app_components/ui/BasicModal';
import { BasicPopover } from '@/app_components/ui/BasicPopover';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { CheckboxV2 } from '@/app_components/ui/CheckboxV2';
import { Form, FormField } from '@/app_components/ui/form';
import { Close } from '@/app_components/ui/primitives/Dialog';
import type { FormValues, OptionWithChecked } from '@/app_lib/utils/dropdownCheckboxUtils';
import {
  buildNested,
  collectAllValues,
  collectLeafIds,
  type FlatSlice,
  getAllSlices,
  getNodeState,
  handleLeafChange,
  handleParentChange,
  type Option,
  shallowEqual,
} from '@/app_lib/utils/dropdownCheckboxUtils';

/**
 * Context providing the slices map to all Content components.
 * A "slice" is the subset of the flat form state (Record<id, boolean>) that contains
 * only the keys for a given option and all its descendants. Slices enable each Content
 * to derive its checked/indeterminate state and re-render only when its slice changes.
 */
const FlatSlicesContext = createContext<Map<string, FlatSlice> | null>(null);

type ContentProps = { option: Option; slice: FlatSlice };

/**
 * Renders a single option (parent or leaf). Receives its "slice" — the subset of flat
 * form state for this option and its descendants — so it can compute checked state
 * and avoid re-renders when unrelated parts of flat change (see React.memo + shallowEqual).
 */
const Content = React.memo(
  ({ option, slice }: ContentProps) => {
    const form = useFormContext<FormValues>();
    const slicesMap = useContext(FlatSlicesContext);

    const state = getNodeState(option, slice); // true | false | 'indeterminate' (derived from slice)
    const hasChildren = Boolean(option.children?.length);

    const handleChange = useCallback(
      (val: 'indeterminate' | boolean) => {
        if (hasChildren) {
          handleParentChange(form, option, val);
        } else {
          handleLeafChange(form, option.value, val);
        }
      },
      [form, option, hasChildren],
    );

    // Keep form value in sync with derived state: true → true, false or indeterminate → false
    useEffect(() => {
      if (!hasChildren) {
        return;
      }
      const formValue = slice[option.value];
      const valueFromState = state === true;
      if (formValue !== valueFromState) {
        form.setValue(`flat.${option.value}` as const, valueFromState, { shouldDirty: true, shouldTouch: true });
      }
    }, [slice, form, hasChildren, option.value, state]);

    const formComponent = (
      <FormField
        name={`flat.${option.value}` as const}
        render={() => (
          <CheckboxV2
            checked={state}
            id={option.value}
            label={option.label}
            onChange={handleChange}
            variant='conferences'
          />
        )}
      />
    );

    return (
      <div className='flex flex-col gap-1'>
        {hasChildren ? (
          <AccordionItem
            className='gap-0'
            key={option.value}
            value={option.value}
          >
            <div className='flex justify-between items-center gap-sm py-xxs'>
              {formComponent}
              <AccordionTrigger className='cursor-pointer'>
                <div className='sr-only'>{option.label}</div>
              </AccordionTrigger>
            </div>
            <AccordionContent custom>
              {option.children?.map((child) => (
                <div
                  className='pl-6 flex flex-col'
                  key={child.title}
                >
                  <span className='typography-xs font-medium font-graphik text-secondary py-2 uppercase'>
                    {child.title}
                  </span>
                  {/* Each child gets its own slice so memo only re-renders when that slice changes. */}
                  <span className='flex flex-col'>
                    {child.options.map((n) => (
                      <Content
                        key={n.value}
                        option={n}
                        slice={slicesMap?.get(n.value) ?? {}}
                      />
                    ))}
                  </span>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ) : (
          <div className='py-xxs'>{formComponent}</div>
        )}
      </div>
    );
  },
  // Re-render only when this option's slice changes; ignore other flat updates.
  (prev, next) => prev.option.value === next.option.value && shallowEqual(prev.slice, next.slice),
);

export const DropdownCheckbox = ({
  className,
  options,
  title,
  onFilterChange,
  resetTrigger,
}: {
  className?: string;
  options: Option[];
  title: string;
  onFilterChange: (filter: OptionWithChecked[]) => void;
  resetTrigger?: number;
}) => {
  // add all options (including children) to the flat object with false values
  const defaultFlat = useMemo(
    () => Object.fromEntries(collectAllValues(options).map((value) => [value, false])),
    [options],
  );

  const form = useForm<FormValues>({ defaultValues: { flat: defaultFlat } });

  const flat = useWatch({ control: form.control, name: 'flat' });
  const timeoutRef = useRef<number | null>(null);
  const isInitialMount = useRef(true);

  const leafIds = useMemo(() => collectLeafIds(options), [options]);
  const count = useMemo(() => leafIds.filter((id) => flat?.[id]).length, [leafIds, flat]);

  /** Map from each option.value to its slice of flat (that option + all descendants). Used by Content for state + memo. */
  const slicesMap = useMemo(() => getAllSlices(options, flat ?? {}), [options, flat]);

  // Debounced: rebuild nested from flat after changes stop for 300ms. Skip the initial mount so we only notify when the user actually changes filters.
  // biome-ignore lint/correctness/useExhaustiveDependencies: <Dont>
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else {
        const nested = buildNested(options, flat ?? {});
        onFilterChange(nested);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [flat, options]);

  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      form.reset({ flat: defaultFlat });
    }
  }, [resetTrigger, form, defaultFlat]);

  const trigger = (
    <ButtonV2
      as='button'
      className='md:p-xxs items-center justify-end hover:no-underline'
      isTertiary
      size='lg'
    >
      <span className='typography-sm group-hover/button:underline'>{title}</span>
      {count > 0 && (
        <span className='typography-xxs font-medium font-graphik flex items-center justify-center text-blue-1 bg-[#E2E4FF] p-[2px] rounded-full size-4'>
          <span className='mt-[1px]'>{count}</span>
        </span>
      )}
      <Icon
        icon='ButtonDown'
        size='custom'
      />
    </ButtonV2>
  );

  const content = (
    <Form {...form}>
      <FlatSlicesContext.Provider value={slicesMap}>
        {/* Root options get their slice from the map; children read from context in Content. */}
        <form className='flex flex-col gap-sm'>
          {options.map((option) => (
            <Content
              key={option.value}
              option={option}
              slice={slicesMap.get(option.value) ?? {}}
            />
          ))}
        </form>
      </FlatSlicesContext.Provider>
    </Form>
  );

  const popoverContent = (
    <AccordionBase
      className='flex flex-col gap-sm! bg-pure rounded-t-md md:rounded-md h-auto shadow-sm p-xs w-screen md:w-[280px]'
      type='multiple'
    >
      {content}
    </AccordionBase>
  );

  const modalContent = (
    <AccordionBase
      className='flex text-primary flex-col gap-sm! bg-pure rounded-t-md md:rounded-md h-[60vh] md:h-auto shadow-sm p-xs w-screen md:w-[280px]'
      type='multiple'
    >
      <div className='flex justify-between items-center gap-sm py-xxs -mr-[12px] -mt-[12px]'>
        <span className='typography-level-5 font-semibold font-graphik'>{title}</span>
        <Close asChild>
          <ActionableIcon>
            <Icon icon='X' />
          </ActionableIcon>
        </Close>
      </div>
      <div className='overflow-y-auto'>{content}</div>
    </AccordionBase>
  );

  return (
    <div className={className}>
      <BasicPopover
        align='end'
        avoidCollisions={false}
        className='hidden md:block'
        content={popoverContent}
        contentClassName='z-body-popover'
        shouldCloseOnScroll={false}
        trigger={trigger}
      />
      <div className='block md:hidden'>
        <BasicModal
          content={modalContent}
          description={title}
          position='bottom'
          title={title}
          trigger={trigger}
        />
      </div>
    </div>
  );
};
